import { FC, ReactNode } from 'react';
import { Box } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { TPaymentMethodType } from 'services/api/paymentMethods/types';
import { AmExSvg } from './AmExSvg';
import { DinersClubSvg } from './DinersClubSvg';
import { DiscoverSvg } from './DiscoverSvg';
import { JCBSvg } from './JCBSvg';
import { MastercardSvg } from './MastercardSvg';
import { UnionPaySvg } from './UnionPaySvg';
import { VisaSvg } from './VisaSvg';

const ICON_HEIGHT = '28px';
const ICON_WIDTH = '46px';

// compat card brand names of the payment gateway ("Visa", "MasterCard", "American Express", ...)
// and normalized brands ("visa", "mastercard", "amex", ...)
const brandSvg = (brand: string | null | undefined): ReactNode => {
	switch ((brand ?? '').toLowerCase().replace(/[\s_-]/g, '')) {
		case 'visa':
			return <VisaSvg />;
		case 'mastercard':
			return <MastercardSvg />;
		case 'americanexpress':
		case 'amex':
			return <AmExSvg />;
		case 'discover':
			return <DiscoverSvg />;
		case 'jcb':
			return <JCBSvg />;
		case 'unionpay':
			return <UnionPaySvg />;
		case 'dinersclub':
		case 'diners':
			return <DinersClubSvg />;
		default:
			return null;
	}
};

const IconBox: FC<{ children: ReactNode }> = ({ children }) => (
	<Box
		component="span"
		sx={{
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: ICON_WIDTH,
			height: ICON_HEIGHT,
			flexShrink: 0,
		}}
	>
		{children}
	</Box>
);

export const CardBrandIcon: FC<{ brand: string | null | undefined }> = ({ brand }) => (
	<IconBox>{brandSvg(brand) ?? <CreditCardIcon color="primary" />}</IconBox>
);

type TPaymentMethodIconProps = {
	type: TPaymentMethodType;
	cardBrand?: string | null;
	bankLogoUrl?: string | null;
	bankName?: string | null;
};

export const PaymentMethodIcon: FC<TPaymentMethodIconProps> = ({
	type,
	cardBrand,
	bankLogoUrl,
	bankName,
}) => {
	switch (type) {
		case 'applepay':
			return (
				<IconBox>
					<AppleIcon sx={{ color: '#000000' }} />
				</IconBox>
			);
		case 'googlepay':
			return (
				<IconBox>
					<GoogleIcon sx={{ color: '#5581a0' }} />
				</IconBox>
			);
		case 'bacs_direct_debit':
			return (
				<IconBox>
					{bankLogoUrl ? (
						<img
							src={bankLogoUrl}
							alt={bankName ?? 'Direct Debit'}
							style={{ maxHeight: ICON_HEIGHT, maxWidth: ICON_WIDTH }}
						/>
					) : (
						<AccountBalanceIcon color="primary" />
					)}
				</IconBox>
			);
		default:
			return <CardBrandIcon brand={cardBrand} />;
	}
};
